import { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import Cropper, { type Area } from 'react-easy-crop';
import { X, Check, Crop, Palette } from 'lucide-react';
import { toast } from 'sonner';

/** Aspect ratio options for cropping */
const ASPECT_OPTIONS = [
  { label: 'Free', value: undefined },
  { label: '1:1', value: 1 },
  { label: '4:5', value: 4 / 5 },
  { label: '16:9', value: 16 / 9 },
] as const;

/** Filter presets (CSS filter values) */
const FILTER_PRESETS: { id: string; label: string; filter: string }[] = [
  { id: 'normal', label: 'Normal', filter: 'none' },
  { id: 'warm', label: 'Warm', filter: 'sepia(0.3) saturate(1.2)' },
  { id: 'cool', label: 'Cool', filter: 'hue-rotate(20deg) saturate(0.9)' },
  { id: 'vintage', label: 'Vintage', filter: 'sepia(0.5) contrast(1.1)' },
  { id: 'bw', label: 'B&W', filter: 'grayscale(1)' },
];

function createImage(url: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    if (url.startsWith('http')) img.crossOrigin = 'anonymous';
    img.addEventListener('load', () => resolve(img));
    img.addEventListener('error', (e) => reject(e));
    img.src = url;
  });
}

async function getCroppedImage(
  imageSrc: string,
  pixelCrop: Area,
  filter: string
): Promise<Blob> {
  const image = await createImage(imageSrc);
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Canvas not supported');

  canvas.width = pixelCrop.width;
  canvas.height = pixelCrop.height;

  if (filter && filter !== 'none') {
    ctx.filter = filter;
  }
  ctx.drawImage(
    image,
    pixelCrop.x,
    pixelCrop.y,
    pixelCrop.width,
    pixelCrop.height,
    0,
    0,
    pixelCrop.width,
    pixelCrop.height
  );

  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) =>
        blob ? resolve(blob) : reject(new Error('Canvas toBlob failed')),
      'image/jpeg',
      0.9
    );
  });
}

interface MediaEditorModalProps {
  /** Image URL (blob or http) – videos are not edited, only shown */
  imageUrl: string;
  isVideo?: boolean;
  onClose: () => void;
  onDone: (file: File) => void;
}

export function MediaEditorModal({
  imageUrl,
  isVideo = false,
  onClose,
  onDone,
}: MediaEditorModalProps) {
  const { t } = useTranslation();
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [aspect, setAspect] = useState<number | undefined>(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<Area | null>(null);
  const [activeFilter, setActiveFilter] = useState('normal');
  const [tab, setTab] = useState<'crop' | 'filter'>('crop');
  const [saving, setSaving] = useState(false);

  const onCropComplete = useCallback(
    (_croppedArea: Area, croppedAreaPx: Area) => {
      setCroppedAreaPixels(croppedAreaPx);
    },
    []
  );

  const onCropAreaChange = useCallback(
    (_croppedArea: Area, croppedAreaPx: Area) => {
      setCroppedAreaPixels(croppedAreaPx);
    },
    []
  );

  const currentFilter =
    FILTER_PRESETS.find((f) => f.id === activeFilter)?.filter ?? 'none';

  const handleDone = async () => {
    if (isVideo) {
      onClose();
      return;
    }
    if (!croppedAreaPixels) {
      toast.error(t('social.adjustCropAreaFirst'));
      return;
    }
    setSaving(true);
    try {
      const blob = await getCroppedImage(
        imageUrl,
        croppedAreaPixels,
        currentFilter
      );
      const file = new File([blob], 'edited-photo.jpg', { type: 'image/jpeg' });
      onDone(file);
      onClose();
    } catch (err) {
      console.error('Crop failed:', err);
    } finally {
      setSaving(false);
    }
  };

  if (isVideo) {
    return (
      <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/80 p-4">
        <div className="rounded-2xl overflow-hidden max-w-lg w-full bg-[#16151D]">
          <div className="flex items-center justify-between p-3 border-b border-white/10">
            <span className="text-white font-medium">{t('social.video')}</span>
            <button
              onClick={onClose}
              className="p-2 rounded-lg hover:bg-white/10 text-white"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
          <div className="aspect-video bg-black">
            <video
              src={imageUrl}
              controls
              className="w-full h-full object-contain"
              playsInline
              muted
              loop
            />
          </div>
          <p className="p-4 text-sm text-white/70">
            {t('social.videoEditingComingSoon')}
          </p>
          <div className="p-4 flex gap-2">
            <button
              onClick={onClose}
              className="flex-1 py-2.5 rounded-xl border border-white/20 text-white"
            >
              {t('social.useAsIs')}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-[60] flex flex-col bg-black">
      {/* Header */}
      <div className="flex items-center justify-between p-4 shrink-0">
        <button
          onClick={onClose}
          className="p-2 rounded-lg hover:bg-white/10 text-white"
        >
          <X className="w-6 h-6" />
        </button>
        <div className="flex gap-2">
          <button
            onClick={() => setTab('crop')}
            className={`flex items-center gap-2 px-4 py-2 rounded-xl ${
              tab === 'crop'
                ? 'bg-[#936FEF] text-white'
                : 'bg-white/10 text-white/80'
            }`}
          >
            <Crop className="w-4 h-4" />
            {t('social.crop')}
          </button>
          <button
            onClick={() => setTab('filter')}
            className={`flex items-center gap-2 px-4 py-2 rounded-xl ${
              tab === 'filter'
                ? 'bg-[#936FEF] text-white'
                : 'bg-white/10 text-white/80'
            }`}
          >
            <Palette className="w-4 h-4" />
            {t('social.filter')}
          </button>
        </div>
        <button
          onClick={handleDone}
          disabled={saving}
          className="flex items-center gap-2 px-4 py-2 rounded-xl bg-[#936FEF] text-white font-medium disabled:opacity-60"
        >
          <Check className="w-5 h-5" />
          {saving ? t('social.saving') : t('common.save')}
        </button>
      </div>

      {/* Crop area */}
      <div className="flex-1 relative min-h-0">
        <div
          className="absolute inset-0"
          style={{ filter: tab === 'filter' ? currentFilter : 'none' }}
        >
          <Cropper
            image={imageUrl}
            crop={crop}
            zoom={zoom}
            aspect={aspect}
            onCropChange={setCrop}
            onZoomChange={setZoom}
            onCropComplete={onCropComplete}
            onCropAreaChange={onCropAreaChange}
            style={{ containerStyle: { backgroundColor: '#000' } }}
          />
        </div>
      </div>

      {/* Controls */}
      <div className="p-4 shrink-0 space-y-4 bg-[#16151D]">
        {tab === 'crop' && (
          <div>
            <p className="text-sm text-white/70 mb-2">
              {t('social.aspectRatio')}
            </p>
            <div className="flex gap-2 flex-wrap">
              {ASPECT_OPTIONS.map((opt) => (
                <button
                  key={opt.label}
                  onClick={() => setAspect(opt.value)}
                  className={`px-4 py-2 rounded-xl text-sm font-medium ${
                    aspect === opt.value
                      ? 'bg-[#936FEF] text-white'
                      : 'bg-white/10 text-white/80 hover:bg-white/15'
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
            <p className="text-xs text-white/50 mt-2">{t('social.zoomHint')}</p>
          </div>
        )}
        {tab === 'crop' && (
          <div>
            <p className="text-sm text-white/70 mb-2">{t('social.zoom')}</p>
            <input
              type="range"
              min={1}
              max={3}
              step={0.1}
              value={zoom}
              onChange={(e) => setZoom(Number(e.target.value))}
              className="w-full h-2 rounded-lg appearance-none cursor-pointer accent-[#936FEF]"
            />
          </div>
        )}
        {tab === 'filter' && (
          <div>
            <p className="text-sm text-white/70 mb-2">{t('social.filter')}</p>
            <div className="flex gap-3 overflow-x-auto pb-2">
              {FILTER_PRESETS.map((preset) => (
                <button
                  key={preset.id}
                  onClick={() => setActiveFilter(preset.id)}
                  className={`flex flex-col items-center gap-1 shrink-0 ${
                    activeFilter === preset.id ? 'opacity-100' : 'opacity-70'
                  }`}
                >
                  <div
                    className="w-14 h-14 rounded-xl overflow-hidden border-2 border-white/30"
                    style={{
                      backgroundImage: `url(${imageUrl})`,
                      backgroundSize: 'cover',
                      backgroundPosition: 'center',
                      filter: preset.filter,
                    }}
                  />
                  <span className="text-xs text-white/80">{preset.label}</span>
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
